import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  deleteOrganisation,
  getOrganisationById,
  previewReleaseAutomationChanges,
  upgradeOrganisation,
  updateOrganisation,
  updateOrganisationSettings,
} from '@/features/organisations/data';
import { getOrganisationEntitlements } from '@/shared/data/entitlements';
import { useAccessToken } from './auth';
import { gridPaths, invalidateGridQuery, queryKeys } from './queryKeys';
import { useInvalidateQueries } from './useInvalidateQueries';
import type { ApiError } from '@/shared/data/client';
import type { OrganisationDto, OrganisationEntitlementsDto } from '@flowqa/shared';

const ORGANISATION_ENTITLEMENTS_STORAGE_KEY = 'flowqa-organisation-entitlements-cache';
const organisationEntitlementsStorageTtlMs = 5 * 60_000;

type StoredOrganisationEntitlementsEntry = {
  savedAt: number;
  data: OrganisationEntitlementsDto;
};

const readStoredOrganisationEntitlements = (
  id: string,
): StoredOrganisationEntitlementsEntry | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(ORGANISATION_ENTITLEMENTS_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Record<string, StoredOrganisationEntitlementsEntry>>;
    const entry = parsed[id];
    if (!entry) return null;
    if (Date.now() - entry.savedAt > organisationEntitlementsStorageTtlMs) {
      return null;
    }
    return entry;
  } catch {
    return null;
  }
};

const writeStoredOrganisationEntitlements = (
  id: string,
  data: OrganisationEntitlementsDto,
): void => {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(ORGANISATION_ENTITLEMENTS_STORAGE_KEY);
    const parsed =
      raw !== null
        ? (JSON.parse(raw) as Partial<Record<string, StoredOrganisationEntitlementsEntry>>)
        : {};
    parsed[id] = {
      savedAt: Date.now(),
      data,
    };
    window.localStorage.setItem(ORGANISATION_ENTITLEMENTS_STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // ignore storage failures
  }
};

export const useOrganisationDetailQuery = (
  id: string | null,
): UseQueryResult<OrganisationDto | null, ApiError> => {
  const token = useAccessToken();
  return useQuery<OrganisationDto | null, ApiError>({
    queryKey: queryKeys.organisationDetail(id),
    enabled: Boolean(token && id),
    queryFn: async () => (id ? await getOrganisationById(id, token ?? undefined) : null),
    staleTime: 30_000,
  });
};

export const useOrganisationEntitlements = (
  id: string | null,
): UseQueryResult<OrganisationEntitlementsDto | null, ApiError> => {
  const token = useAccessToken();
  return useQuery<OrganisationEntitlementsDto | null, ApiError>({
    queryKey: queryKeys.organisationEntitlements(id),
    enabled: Boolean(token && id),
    queryFn: async () => {
      if (!id) {
        return null;
      }
      const result = await getOrganisationEntitlements(id, token ?? undefined);
      writeStoredOrganisationEntitlements(id, result);
      return result;
    },
    initialData: id ? (readStoredOrganisationEntitlements(id)?.data ?? undefined) : undefined,
    staleTime: 5 * 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useUpdateOrganisationSettings = (): UseMutationResult<
  OrganisationDto,
  ApiError,
  {
    id: string;
    automateReleaseStatuses: boolean;
    requireFeaturesForReady: boolean;
    requireReadyFeaturesForReady: boolean;
    recomputeReleaseStatuses?: boolean;
    recomputeReleaseIds?: string[];
  }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  return useMutation<
    OrganisationDto,
    ApiError,
    {
      id: string;
      automateReleaseStatuses: boolean;
      requireFeaturesForReady: boolean;
      requireReadyFeaturesForReady: boolean;
      recomputeReleaseStatuses?: boolean;
      recomputeReleaseIds?: string[];
    }
  >({
    mutationFn: async (input) => await updateOrganisationSettings(input),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.organisationDetail(updated.id), updated);
      await invalidateQueries([invalidateGridQuery(gridPaths.organisations)]);
    },
  });
};

export const useUpdateOrganisation = (): UseMutationResult<
  OrganisationDto,
  ApiError,
  { id: string; name: string }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  return useMutation<OrganisationDto, ApiError, { id: string; name: string }>({
    mutationFn: async (input) => await updateOrganisation(input),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.organisationDetail(updated.id), updated);
      await invalidateQueries([invalidateGridQuery(gridPaths.organisations)]);
    },
  });
};

export const useDeleteOrganisation = (): UseMutationResult<
  { status: string },
  ApiError,
  { id: string }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  return useMutation<{ status: string }, ApiError, { id: string }>({
    mutationFn: async (input) => await deleteOrganisation(input),
    onSuccess: async (_payload, input) => {
      queryClient.removeQueries({ queryKey: queryKeys.organisationDetail(input.id) });
      await invalidateQueries([invalidateGridQuery(gridPaths.organisations)]);
    },
  });
};

export const useReleaseAutomationPreview = (): UseMutationResult<
  {
    items: Array<{
      id: string;
      slug: string;
      name: string;
      projectId: string;
      projectName: string;
      currentStatus: string;
      nextStatus: string;
    }>;
  },
  ApiError,
  {
    id: string;
    automateReleaseStatuses: boolean;
    requireFeaturesForReady: boolean;
    requireReadyFeaturesForReady: boolean;
  }
> => {
  return useMutation({
    mutationFn: async (input) => await previewReleaseAutomationChanges(input),
  });
};

export const useUpgradeOrganisation = (): UseMutationResult<
  OrganisationDto,
  ApiError,
  {
    id: string;
    name: string;
    planCode: OrganisationEntitlementsDto['plan']['code'];
    addOns?: { deliveryEnabled?: boolean; operationsEnabled?: boolean };
  }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  return useMutation<
    OrganisationDto,
    ApiError,
    {
      id: string;
      name: string;
      planCode: OrganisationEntitlementsDto['plan']['code'];
      addOns?: { deliveryEnabled?: boolean; operationsEnabled?: boolean };
    }
  >({
    mutationFn: async (input) => await upgradeOrganisation(input),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.organisationDetail(updated.id), updated);
      await invalidateQueries([invalidateGridQuery(gridPaths.organisations)]);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.organisationEntitlements(updated.id),
      });
    },
  });
};
